import { useState } from 'react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { AlertTriangle } from 'lucide-react';
import type { TrashItem } from '@/hooks/useTrash';

interface PermanentDeleteDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  item: TrashItem | null;
  onConfirm: () => void;
  isDeleting: boolean;
}

export function PermanentDeleteDialog({
  open,
  onOpenChange,
  item,
  onConfirm,
  isDeleting,
}: PermanentDeleteDialogProps) {
  const [confirmText, setConfirmText] = useState('');
  
  const isConfirmValid = confirmText === 'DELETE';

  const handleOpenChange = (newOpen: boolean) => {
    if (!newOpen) {
      setConfirmText('');
    }
    onOpenChange(newOpen);
  };

  const handleConfirm = () => {
    if (isConfirmValid) {
      onConfirm();
      setConfirmText('');
    }
  };

  if (!item) return null;

  return (
    <AlertDialog open={open} onOpenChange={handleOpenChange}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <div className="flex items-center gap-3 text-destructive">
            <AlertTriangle className="h-6 w-6" />
            <AlertDialogTitle>Xóa vĩnh viễn</AlertDialogTitle>
          </div>
          <AlertDialogDescription className="space-y-3 pt-2">
            <p>
              Bạn sắp xóa vĩnh viễn <strong>"{item.name}"</strong>.
            </p>
            <div className="rounded-md bg-destructive/10 p-3 text-destructive text-sm">
              <strong>⚠️ Cảnh báo:</strong> Hành động này không thể hoàn tác. 
              Dữ liệu sẽ bị xóa hoàn toàn khỏi hệ thống.
            </div>
          </AlertDialogDescription>
        </AlertDialogHeader>
        
        <div className="space-y-2 py-2">
          <Label htmlFor="confirm-delete">
            Gõ <strong className="text-destructive">DELETE</strong> để xác nhận:
          </Label>
          <Input
            id="confirm-delete"
            value={confirmText}
            onChange={(e) => setConfirmText(e.target.value)}
            placeholder="DELETE"
            className="font-mono"
            autoComplete="off"
          />
        </div>

        <AlertDialogFooter>
          <AlertDialogCancel disabled={isDeleting}>Hủy</AlertDialogCancel>
          <AlertDialogAction
            onClick={handleConfirm}
            disabled={!isConfirmValid || isDeleting}
            className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
          >
            {isDeleting ? 'Đang xóa...' : 'Xóa vĩnh viễn'}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
